/**
 * MobileNavigationTab.jsx - Navegación Táctil Optimizada
 * 
 * Sistema de navegación bottom-tab especializado para móviles con:
 * - Tabs fijos en la parte inferior con zona táctil mínima 44px
 * - Swipe gestures para cambio rápido entre paneles  
 * - Indicadores visuales de estado activo con animaciones suaves
 * - Breadcrumbs colapsables para navegación jerárquica
 * - Quick actions contextuales según la pantalla actual
 * - Vibration feedback en dispositivos compatibles
 * 
 * Características avanzadas:
 * - Auto-hide cuando aparece teclado virtual
 * - Badge notifications para elementos pendientes
 * - Sliding panel transitions con easing suave
 * - Adaptive icons según estado de conectividad
 * - Emergency back button para casos navigationStack corrupto
 * 
 * Integración con sistemas:
 * - CrossFeedbackPanel: Navegación entre evaluate/sent/received
 * - WeeklyDeliverableEditor: Cambio rápido entre paneles B1-B4
 * - GroupDashboard: Filtros rápidos y acciones bulk
 * - Coach IA: Acceso directo a análisis y feedback
 * 
 * @component
 * @props {Array} tabs - Configuración de tabs con { id, icon, label, badge?, disabled? }
 * @props {string} activeTab - ID del tab actualmente activo
 * @props {function} onTabChange - Callback cuando cambia el tab activo
 * @props {Object} quickActions - Acciones contextuales del panel actual
 * @props {boolean} enableSwipe - Habilitar navegación por swipe (default: true)
 * @author Sistema EPL - Mobile UX
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Home, Users, MessageSquare, BarChart3, Settings, 
  ChevronLeft, ChevronRight, MoreHorizontal, Zap,
  Wifi, WifiOff, Battery, BatteryLow, Bell
} from 'lucide-react';
import { useMobile } from '../context/MobileOptimizationProvider';

const MobileNavigationTab = ({ 
  tabs = [],
  activeTab = '', 
  onTabChange = () => {},
  quickActions = {},
  enableSwipe = true,
  showBreadcrumbs = true,
  showQuickActions = true,
  className = ''
}) => {
  // Mobile context
  const { 
    isMobile, 
    keyboardVisible, 
    handleTouchGestures, 
    getMobileClasses,
    isTouchDevice 
  } = useMobile();

  // Estados locales
  const [isVisible, setIsVisible] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Referencias para swipe handling
  const navRef = useRef(null);
  const swipeStartRef = useRef(null);

  /**
   * Detectar estado de conectividad y batería
   */
  useEffect(() => {
    // Connection status
    const updateConnection = () => {
      setConnectionStatus(navigator.onLine ? 'online' : 'offline');
    };
    
    window.addEventListener('online', updateConnection);
    window.addEventListener('offline', updateConnection);
    updateConnection();

    // Battery API (si está disponible)
    if ('getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        setBatteryLevel(Math.round(battery.level * 100));
        
        const updateBattery = () => {
          setBatteryLevel(Math.round(battery.level * 100));
        };
        
        battery.addEventListener('levelchange', updateBattery);
        
        return () => {
          battery.removeEventListener('levelchange', updateBattery);
        };
      });
    }

    return () => {
      window.removeEventListener('online', updateConnection);
      window.removeEventListener('offline', updateConnection);
    };
  }, []);

  /**
   * Ocultar navegación cuando aparece el teclado
   */
  useEffect(() => {
    if (keyboardVisible) {
      setIsVisible(false);
    } else {
      // Delay para evitar flickering
      const timer = setTimeout(() => setIsVisible(true), 200);
      return () => clearTimeout(timer);
    }
  }, [keyboardVisible]);

  /**
   * Setup de swipe gestures
   */
  useEffect(() => {
    if (!enableSwipe || !isTouchDevice || !navRef.current) return;

    const cleanup = handleTouchGestures(navRef.current, {
      threshold: 50,
      timeThreshold: 300,
      onSwipe: (direction, delta) => {
        if (Math.abs(delta) < 80) return; // Require minimum swipe distance
        
        const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
        let newIndex;
        
        if (direction === 'left' && currentIndex < tabs.length - 1) {
          newIndex = currentIndex + 1;
        } else if (direction === 'right' && currentIndex > 0) {
          newIndex = currentIndex - 1;
        }
        
        if (newIndex !== undefined) {
          // Vibration feedback si está disponible
          if ('vibrate' in navigator) {
            navigator.vibrate(50);
          }
          
          setIsTransitioning(true);
          onTabChange(tabs[newIndex].id);
          
          setTimeout(() => setIsTransitioning(false), 300);
        }
      }
    });

    return cleanup;
  }, [enableSwipe, isTouchDevice, tabs, activeTab, onTabChange, handleTouchGestures]);

  /**
   * Obtener icono según el tipo
   */
  const getTabIcon = useCallback((tab) => {
    const iconMap = {
      home: Home,
      users: Users,
      groups: Users,
      messages: MessageSquare,
      feedback: MessageSquare,
      analytics: BarChart3,
      dashboard: BarChart3,
      settings: Settings,
      more: MoreHorizontal
    };

    const IconComponent = iconMap[tab.icon] || Home;
    return <IconComponent className="w-5 h-5" />;
  }, []);

  /**
   * Calcular badge total de notificaciones
   */
  const getTotalBadges = useCallback(() => {
    return tabs.reduce((total, tab) => total + (tab.badge || 0), 0);
  }, [tabs]);

  /**
   * Renderizar breadcrumbs colapsables
   */
  const renderBreadcrumbs = () => {
    if (!showBreadcrumbs || !isMobile) return null;

    return (
      <div className="flex items-center py-1 px-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        <div className="flex items-center space-x-1 text-xs text-gray-600 overflow-hidden">
          <Home className="w-3 h-3 flex-shrink-0" />
          <ChevronRight className="w-3 h-3 flex-shrink-0" />
          <span className="truncate font-medium text-blue-600">
            {tabs.find(tab => tab.id === activeTab)?.label || 'Página'}
          </span>
          
          {/* Connection status indicator */}
          <div className="ml-auto flex items-center space-x-1">
            {connectionStatus === 'offline' ? (
              <WifiOff className="w-3 h-3 text-red-500" />
            ) : (
              <Wifi className="w-3 h-3 text-green-500" />
            )}
            
            {batteryLevel !== null && batteryLevel < 20 && (
              <BatteryLow className="w-3 h-3 text-yellow-500" />
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renderizar quick actions
   */
  const renderQuickActions = () => {
    if (!showQuickActions || !quickActions.actions?.length) return null;

    return (
      <div className="flex justify-center py-2 px-4 bg-gray-50 border-b border-gray-200">
        <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
          {quickActions.actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className={getMobileClasses(`
                flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium
                flex items-center space-x-1 transition-colors duration-200
                ${action.disabled 
                  ? 'bg-gray-200 text-gray-400' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 active:bg-blue-300'
                }
              `, { touchTarget: true })}
            >
              {action.icon && <span className="w-3 h-3">{action.icon}</span>}
              <span>{action.label}</span>
              {action.badge && (
                <span className="bg-red-500 text-white text-xs rounded-full px-1 min-w-[16px] h-4 flex items-center justify-center">
                  {action.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renderizar tabs principales
   */
  const renderTabs = () => {
    return (
      <div 
        ref={navRef}
        className={getMobileClasses(`
          flex justify-around items-center py-2 px-4
          bg-white border-t border-gray-200 shadow-lg
          ${isTransitioning ? 'opacity-75' : ''}
        `)}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          
          return (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && onTabChange(tab.id)}
              disabled={tab.disabled}
              className={getMobileClasses(`
                flex flex-col items-center justify-center space-y-1
                p-2 rounded-lg transition-all duration-200 relative
                ${isActive 
                  ? 'text-blue-600 bg-blue-50 scale-105' 
                  : tab.disabled
                    ? 'text-gray-400'
                    : 'text-gray-600 hover:text-blue-500 hover:bg-gray-50'
                }
                ${isTransitioning ? 'pointer-events-none' : ''}
              `, { touchTarget: true })}
            >
              {/* Tab icon */}
              <div className="relative">
                {getTabIcon(tab)}
                
                {/* Badge indicator */}
                {tab.badge && tab.badge > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1 min-w-[16px] h-4 flex items-center justify-center">
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </div>
                )}
                
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full" />
                )}
              </div>
              
              {/* Tab label */}
              <span className="text-xs font-medium truncate max-w-[60px]">
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  // No renderizar si no es móvil o si no hay tabs
  if (!isMobile || tabs.length === 0) {
    return null;
  }

  return (
    <div 
      className={`
        fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300
        ${isVisible ? 'translate-y-0' : 'translate-y-full'}
        ${className}
      `}
    >
      {/* Breadcrumbs */}
      {renderBreadcrumbs()}
      
      {/* Quick actions */}
      {renderQuickActions()}
      
      {/* Main navigation tabs */}
      {renderTabs()}
      
      {/* Total notifications indicator */}
      {getTotalBadges() > 0 && (
        <div className="absolute -top-2 right-4">
          <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-lg animate-pulse">
            {getTotalBadges()} pendiente{getTotalBadges() !== 1 ? 's' : ''}
          </div>
        </div>
      )}
      
      {/* Emergency connection warning */}
      {connectionStatus === 'offline' && (
        <div className="absolute -top-8 left-4 right-4 bg-red-500 text-white text-xs py-1 px-2 rounded text-center animate-bounce">
          Sin conexión - Cambios no guardados
        </div>
      )}
    </div>
  );
};

/**
 * Hook para configurar tabs automáticamente según el componente actual
 */
export const useMobileNavTabs = (componentType, extraConfig = {}) => {
  const [activeTab, setActiveTab] = useState(extraConfig.defaultTab || '');
  
  const getTabsConfig = useCallback(() => {
    switch (componentType) {
      case 'crossFeedback':
        return {
          tabs: [
            { id: 'evaluate', icon: 'feedback', label: 'Evaluar', badge: extraConfig.pendingEvaluations },
            { id: 'sent', icon: 'messages', label: 'Enviados', badge: extraConfig.sentCount },
            { id: 'received', icon: 'users', label: 'Recibidos', badge: extraConfig.receivedCount },
            { id: 'matrix', icon: 'analytics', label: 'Matriz' }
          ],
          quickActions: {
            actions: [
              { label: 'Guardar', icon: '💾', onClick: extraConfig.onSave },
              { label: 'Actualizar', icon: '🔄', onClick: extraConfig.onRefresh },
              { label: 'Ayuda', icon: '❓', onClick: extraConfig.onHelp }
            ]
          }
        };
        
      case 'weeklyEditor':
        return {
          tabs: [
            { id: 'content', icon: 'home', label: 'Contenido' },
            { id: 'collaborate', icon: 'users', label: 'Colaborar' },
            { id: 'feedback', icon: 'messages', label: 'Feedback' },
            { id: 'coach', icon: 'settings', label: 'Coach IA', badge: extraConfig.aiSuggestions }
          ],
          quickActions: {
            actions: [
              { label: 'Autoguardar', icon: '💾', disabled: !extraConfig.hasChanges },
              { label: 'IA Ayuda', icon: '🤖', onClick: extraConfig.onAIHelp, badge: extraConfig.aiAvailable ? 1 : 0 }
            ]
          }
        };
        
      case 'groupDashboard':
        return {
          tabs: [
            { id: 'overview', icon: 'dashboard', label: 'Resumen' },
            { id: 'groups', icon: 'users', label: 'Grupos', badge: extraConfig.activeGroups },
            { id: 'analytics', icon: 'analytics', label: 'Análisis' },
            { id: 'alerts', icon: 'more', label: 'Alertas', badge: extraConfig.alertCount }
          ],
          quickActions: {
            actions: [
              { label: 'Filtrar', icon: '🔍', onClick: extraConfig.onFilter },
              { label: 'Exportar', icon: '📊', onClick: extraConfig.onExport },
              { label: 'Actualizar', icon: '🔄', onClick: extraConfig.onRefresh }
            ]
          }
        };
        
      default:
        return {
          tabs: [
            { id: 'main', icon: 'home', label: 'Principal' }
          ],
          quickActions: { actions: [] }
        };
    }
  }, [componentType, extraConfig]);
  
  return {
    ...getTabsConfig(),
    activeTab,
    onTabChange: setActiveTab
  };
};

export default MobileNavigationTab;